import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUp,
  BarChart3,
  Calendar,
  CalendarPlus,
  Camera,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  CreditCard,
  Crown,
  DollarSign,
  Download,
  Edit,
  ExternalLink,
  Eye,
  Heart,
  ImagePlus,
  Images,
  Lock,
  MessageSquare,
  Plus,
  Search,
  Send,
  Settings,
  Sparkles,
  Star,
  Upload,
  User,
  Users,
  Wallet,
  X,
  Zap
} from "lucide-react";

const fallbackImages = [
  "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=900",
  "https://images.unsplash.com/photo-1512316609839-ce289d3eba0a?auto=format&fit=crop&q=80&w=900",
  "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&q=80&w=900",
  "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&q=80&w=900",
  "https://images.unsplash.com/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&q=80&w=900",
  "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?auto=format&fit=crop&q=80&w=900"
];

const money = (value) => `$${Number(value || 0).toLocaleString()}`;

const shortDate = (value, fallback = "May 18, 2024") => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const longDate = (value, fallback = "Thu, June 4, 2026") => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });
};

const statusLabel = (status = "confirmed") => String(status).replace(/([A-Z])/g, " $1").replace(/_/g, " ").trim();

const statusTone = (status = "") => {
  if (["paid", "completed", "confirmed", "approved", "signed"].includes(status)) return "green";
  if (["pending", "pendingSignature", "in_progress"].includes(status)) return "purple";
  if (["declined", "cancelled"].includes(status)) return "red";
  return "stone";
};

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const MiniSparkline = ({ bars = false }) => (
  bars ? (
    <div className="aura-pro-mini-bars" aria-hidden="true">
      {[34, 48, 58, 26, 64, 42, 72, 38, 78, 92].map((height, index) => (
        <i key={index} style={{ height: `${height}%` }} />
      ))}
    </div>
  ) : (
    <svg className="aura-pro-sparkline" viewBox="0 0 160 64" aria-hidden="true">
      <path d="M4 52 C24 32 34 28 48 38 S74 54 92 28 S116 16 130 30 S148 34 156 12" />
      <circle cx="156" cy="12" r="4" />
    </svg>
  )
);

const SectionHead = ({ title, subtitle, action }) => (
  <div className="aura-pro-section-head">
    <div>
      <h2 className="serif">{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
    {action}
  </div>
);

const StatCard = ({ icon: Icon, label, value, trend, detail, bars }) => (
  <article className="aura-pro-stat-card">
    <span className="aura-pro-stat-icon">{Icon && <Icon className="w-5 h-5" />}</span>
    <div>
      <p>{label}</p>
      <strong className="serif">{value}</strong>
      {trend ? (
        <small className="is-positive"><ArrowUp className="w-3 h-3" />{trend}</small>
      ) : (
        detail && <small>{detail}</small>
      )}
    </div>
    <MiniSparkline bars={bars} />
  </article>
);

const StatusPill = ({ children, tone = "stone" }) => (
  <span className={`aura-pro-pill aura-pro-pill-${tone}`}>{children}</span>
);

const Avatar = ({ name, getInitials, image, getImageUrl }) => {
  const src = image ? getImageUrl(image) : null;
  return src ? (
    <img className="aura-pro-avatar" src={src} alt={name || "Client"} />
  ) : (
    <span className="aura-pro-avatar">{getInitials(name)}</span>
  );
};

const CompactButton = ({ children, dark = false, className = "", ...props }) => (
  <button {...props} className={`aura-pro-btn ${dark ? "is-dark" : ""} ${className}`}>
    {children}
  </button>
);

const Progress = ({ value = 0 }) => (
  <div className="aura-pro-progress">
    <span style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
  </div>
);

const EmptyState = ({ icon: Icon, title, copy, action }) => (
  <div className="aura-pro-empty">
    {Icon && <Icon className="w-9 h-9" />}
    <h3 className="serif">{title}</h3>
    <p>{copy}</p>
    {action}
  </div>
);

const ProRedesignSections = (props) => {
  const {
    activeSection,
    stats,
    conversations,
    jobs,
    bookings,
    myAvailability,
    availabilityMonth,
    setAvailabilityMonth,
    availabilityYear,
    setAvailabilityYear,
    myGalleries,
    portfolioImages,
    newImageUrl,
    setNewImageUrl,
    uploading,
    profileImage,
    profileInputRef,
    portfolioInputRef,
    handleProfilePhotoUpload,
    handlePortfolioUpload,
    addPortfolioImage,
    removePortfolioImage,
    user,
    subscription,
    subscriptionLoading,
    boostPricing,
    formatBoostPrice,
    handleChangePlan,
    handleActivateBoost,
    handlePurchaseBoosts,
    openCreateGallery,
    copyGalleryLink,
    sendGalleryLink,
    openEditGallery,
    deleteGallery,
    copyingGalleryId,
    sharingGalleryId,
    deletingGalleryId,
    openChat,
    selectedConversation,
    chatMessages,
    newMessage,
    setNewMessage,
    sendChatMessage,
    sendingMessage,
    updateJobStatus,
    openDeliveryModal,
    toggleDateAvailability,
    handleApproveBooking,
    handleRejectBooking,
    getInitials,
    getImageUrl,
    setActiveSection,
    showGalleryNotice,
    studioName,
    profileCompletion,
    totalRevenue,
    formattedProfileViews
  } = props;

  const imagePool = portfolioImages.length > 0 ? portfolioImages : fallbackImages;
  const completedJobs = jobs.filter((job) => job.status === "completed");
  const pendingBookings = bookings.filter((booking) => booking.status === "pending");
  const activeBookings = bookings.filter((booking) => ["approved", "pendingSignature", "signed", "confirmed", "paid"].includes(booking.status));
  const activeJobs = jobs.filter((job) => job.status !== "cancelled");
  const averageJob = (stats?.completed_jobs || completedJobs.length) > 0
    ? Math.round(Number(totalRevenue || 0) / Number(stats?.completed_jobs || completedJobs.length || 1))
    : Number(user?.hourly_rate || 700);
  const notify = showGalleryNotice || (() => {});
  const [galleryQuery, setGalleryQuery] = useState("");
  const [galleryFilter, setGalleryFilter] = useState("All");
  const [gallerySort, setGallerySort] = useState("newest");
  const [messageQuery, setMessageQuery] = useState("");
  const [messageFilter, setMessageFilter] = useState("All");
  const [portfolioQuery, setPortfolioQuery] = useState("");
  const [portfolioFilter, setPortfolioFilter] = useState("All");

  const filteredGalleries = useMemo(() => {
    const query = galleryQuery.trim().toLowerCase();
    const filtered = myGalleries.filter((gallery) => {
      const status = String(gallery.status || "private").toLowerCase();
      const delivered = gallery.delivered_images?.length > 0;
      const matchesFilter =
        galleryFilter === "All" ||
        (galleryFilter === "Delivered" && delivered) ||
        status === galleryFilter.toLowerCase();
      const searchable = [
        gallery.title,
        gallery.client_name,
        gallery.client_email,
        gallery.description,
        status
      ].filter(Boolean).join(" ").toLowerCase();
      return matchesFilter && (!query || searchable.includes(query));
    });

    return [...filtered].sort((a, b) => {
      const aTime = new Date(a.created_at || a.completed_at || 0).getTime();
      const bTime = new Date(b.created_at || b.completed_at || 0).getTime();
      return gallerySort === "newest" ? bTime - aTime : aTime - bTime;
    });
  }, [galleryFilter, galleryQuery, gallerySort, myGalleries]);

  const filteredConversations = useMemo(() => {
    const query = messageQuery.trim().toLowerCase();
    return conversations.filter((conversation) => {
      const hasLastMessage = Boolean(conversation.last_message);
      const messageText = String(conversation.last_message || "").toLowerCase();
      const searchable = [
        conversation.client_name,
        conversation.client_email,
        conversation.photographer_name,
        conversation.other_user_name,
        conversation.last_message
      ].filter(Boolean).join(" ").toLowerCase();

      const matchesFilter =
        messageFilter === "All" ||
        (messageFilter === "Unread" && hasLastMessage) ||
        (messageFilter === "Clients" && Boolean(conversation.client_name || conversation.client_email)) ||
        (messageFilter === "Inquiries" && /inquir|book|session|quote|avail/.test(messageText)) ||
        (messageFilter === "Delivered" && /gallery|delivered|ready|archive/.test(messageText));

      return matchesFilter && (!query || searchable.includes(query));
    });
  }, [conversations, messageFilter, messageQuery]);

  const portfolioItems = useMemo(() => {
    const titles = ["Timeless Romance", "Golden Hour Portraits", "Editorial Noir", "Brand Story"];
    const categories = ["Weddings", "Portraits", "Editorial", "Branding"];
    const query = portfolioQuery.trim().toLowerCase();

    return portfolioImages
      .map((img, index) => ({
        img,
        index,
        title: titles[index % titles.length],
        category: categories[index % categories.length]
      }))
      .filter((item) => {
        const matchesFilter = portfolioFilter === "All" || item.category === portfolioFilter;
        const searchable = `${item.title} ${item.category}`.toLowerCase();
        return matchesFilter && (!query || searchable.includes(query));
      });
  }, [portfolioFilter, portfolioImages, portfolioQuery]);

  if (activeSection === "galleries") {
    return (
      <section className="aura-pro-section" data-testid="galleries-section">
        <SectionHead
          title="Client Galleries"
          subtitle="Manage, share, and deliver private client collections."
          action={(
            <CompactButton dark onClick={openCreateGallery} data-testid="new-gallery-btn">
              <Plus className="w-4 h-4" /> New Gallery
            </CompactButton>
          )}
        />

        <div className="aura-pro-stat-grid three">
          <StatCard icon={Images} label="Total Galleries" value={myGalleries.length || 0} trend="16% from last month" />
          <StatCard icon={Send} label="Pending Delivery" value={pendingBookings.length || 0} detail="Needs your attention" />
          <StatCard icon={Users} label="Shared This Week" value={Math.min(myGalleries.length, 12)} trend="33% from last week" />
        </div>

        <div className="aura-pro-panel aura-archive-panel">
          <div className="aura-pro-toolbar">
            <label className="aura-pro-search">
              <Search className="w-4 h-4" />
              <input
                placeholder="Search galleries..."
                aria-label="Search galleries"
                value={galleryQuery}
                onChange={(event) => setGalleryQuery(event.target.value)}
              />
            </label>
            <div className="aura-pro-tabs">
              {["All", "Private", "Shared", "Draft", "Delivered"].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setGalleryFilter(tab)}
                  className={galleryFilter === tab ? "is-active" : ""}
                >
                  {tab}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="aura-pro-select"
              onClick={() => setGallerySort((current) => current === "newest" ? "oldest" : "newest")}
            >
              {gallerySort === "newest" ? "Newest first" : "Oldest first"} <ChevronRight className="w-4 h-4 rotate-90" />
            </button>
          </div>

          {myGalleries.length === 0 ? (
            <EmptyState
              icon={Images}
              title="No private galleries yet"
              copy="Create a client gallery, upload finished images, then send the private link directly in messages."
              action={(
                <CompactButton dark onClick={openCreateGallery}>
                  <Plus className="w-4 h-4" /> Create Gallery
                </CompactButton>
              )}
            />
          ) : filteredGalleries.length === 0 ? (
            <EmptyState
              icon={Search}
              title="No galleries match"
              copy="Adjust the search, filter, or sort to find a different client collection."
            />
          ) : (
            <div className="aura-pro-gallery-grid">
              {filteredGalleries.slice(0, 6).map((gallery, index) => {
                const cover = gallery.delivered_images?.[0] || imagePool[index % imagePool.length];
                return (
                  <article key={gallery.id} className="aura-pro-gallery-card" data-testid={`gallery-card-${gallery.id}`}>
                    <div className="aura-pro-gallery-cover">
                      <img src={getImageUrl(cover)} alt={gallery.title || "Client gallery"} />
                      {index === 0 && <StatusPill tone="purple"><Sparkles className="w-3 h-3" /> Featured</StatusPill>}
                      <span className="aura-image-count">{gallery.delivered_images?.length || 0} images</span>
                    </div>
                    <div className="aura-pro-gallery-body">
                      <div>
                        <h3 className="serif">{gallery.title || "Private Collection"}</h3>
                        <StatusPill tone={gallery.status === "shared" ? "blue" : "green"}>{gallery.status || "Private"}</StatusPill>
                      </div>
                      <p>{gallery.client_name || gallery.client_email}</p>
                      <small>Created by {studioName}</small>
                      <div className="aura-pro-action-row">
                        <Link to={`/gallery/${gallery.id}`} className="aura-pro-btn" data-testid={`open-gallery-${gallery.id}`}>
                          <ExternalLink className="w-4 h-4" /> Open
                        </Link>
                        <CompactButton onClick={() => copyGalleryLink(gallery)} disabled={copyingGalleryId === gallery.id} data-testid={`copy-gallery-${gallery.id}`}>
                          <Copy className="w-4 h-4" /> {copyingGalleryId === gallery.id ? "Copied" : "Copy Link"}
                        </CompactButton>
                        <CompactButton dark onClick={() => sendGalleryLink(gallery)} disabled={sharingGalleryId === gallery.id} data-testid={`send-gallery-${gallery.id}`}>
                          <Send className="w-4 h-4" /> {sharingGalleryId === gallery.id ? "Sending" : "Send"}
                        </CompactButton>
                      </div>
                      <div className="aura-pro-card-foot">
                        <span>{shortDate(gallery.created_at, "Recently")}</span>
                        <div>
                          <button onClick={() => openEditGallery(gallery)} title="Edit gallery" data-testid={`edit-gallery-${gallery.id}`}><Edit className="w-4 h-4" /></button>
                          <button onClick={() => deleteGallery(gallery.id)} disabled={deletingGalleryId === gallery.id} title="Delete gallery" data-testid={`delete-gallery-${gallery.id}`}><X className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    );
  }

  if (activeSection === "bookings") {
    const visibleSchedule = [...activeBookings, ...pendingBookings].slice(0, 4);
    return (
      <section className="aura-pro-section" data-testid="bookings-section">
        <SectionHead title="Bookings" subtitle="Manage sessions, availability, and client requests." />
        <div className="aura-pro-stat-grid four">
          <StatCard icon={Calendar} label="Total Bookings" value={bookings.length + jobs.length} trend="18% from last month" />
          <StatCard icon={Clock} label="Upcoming This Week" value={activeBookings.length || 0} detail="2 today • 4 later" bars />
          <StatCard icon={Send} label="Pending Requests" value={pendingBookings.length} detail="Awaiting your response" />
          <StatCard icon={DollarSign} label="Revenue Booked" value={money(totalRevenue)} trend="24% from last month" />
        </div>

        <div className="aura-pro-layout two">
          <div className="aura-pro-stack">
            <article className="aura-pro-panel">
              <div className="aura-pro-panel-head">
                <h3 className="serif">Today&apos;s Schedule</h3>
                <button onClick={() => setActiveSection("availability")}>View Calendar <ArrowRight className="w-4 h-4" /></button>
              </div>
              {visibleSchedule.length === 0 ? (
                <EmptyState icon={Calendar} title="No sessions scheduled" copy="Approved bookings will appear here with their payment and delivery status." />
              ) : visibleSchedule.map((booking) => {
                const isPending = booking.status === "pending";
                return (
                  <div key={booking.id} className="aura-pro-booking-row">
                    <Avatar name={booking.client_name} getInitials={getInitials} />
                    <div>
                      <h4 className="serif">{booking.client_name || "Client Session"}</h4>
                      <p>{booking.session_type || booking.title || "Portrait Session"}</p>
                      <small>{booking.notes || booking.description || "Studio session"}</small>
                    </div>
                    <div className="aura-pro-row-meta">
                      <span><Calendar className="w-4 h-4" /> {longDate(booking.date || booking.booking_date)}</span>
                      <span><Clock className="w-4 h-4" /> {booking.time || "10:00 AM - 12:00 PM"}</span>
                    </div>
                    <StatusPill tone={statusTone(booking.status)}>{isPending ? "Pending Approval" : statusLabel(booking.status || "confirmed")}</StatusPill>
                    <div className="aura-pro-row-actions">
                      {isPending ? (
                        <>
                          <CompactButton dark onClick={() => handleApproveBooking(booking.id)} data-testid={`approve-booking-${booking.id}`}>Accept</CompactButton>
                          <CompactButton onClick={() => handleRejectBooking(booking.id)} data-testid={`reject-booking-${booking.id}`}>Decline</CompactButton>
                        </>
                      ) : (
                        <CompactButton onClick={() => notify("Booking details are already visible in this row.")}>View Details</CompactButton>
                      )}
                    </div>
                  </div>
                );
              })}
            </article>

            <article className="aura-pro-panel">
              <div className="aura-pro-panel-head">
                <h3 className="serif">Jobs / Projects</h3>
                <span>{activeJobs.length} active</span>
              </div>
              {activeJobs.length === 0 ? (
                <EmptyState icon={Camera} title="No active projects" copy="When a booking becomes a job, editing and gallery delivery controls will live here." />
              ) : activeJobs.slice(0, 5).map((job) => (
                <div key={job.id} className="aura-pro-job-row" data-testid={`booking-${job.id}`}>
                  <Avatar name={job.client_name || job.title} getInitials={getInitials} />
                  <div>
                    <h4>{job.title || "Client Project"}</h4>
                    <p>{shortDate(job.scheduled_date || job.created_at)} • {job.delivered_images?.length || 0} images</p>
                  </div>
                  <div className="aura-pro-job-progress">
                    {["Shoot", "Editing", "Delivery"].map((step, index) => (
                      <span key={step} className={index < (job.status === "completed" ? 3 : job.status === "in_progress" ? 2 : 1) ? "is-done" : ""}>{step}</span>
                    ))}
                  </div>
                  <StatusPill tone={statusTone(job.status)}>{statusLabel(job.status)}</StatusPill>
                  {job.status === "pending" && <CompactButton onClick={() => updateJobStatus(job.id, "confirmed")}>Confirm</CompactButton>}
                  {job.status === "confirmed" && <CompactButton onClick={() => updateJobStatus(job.id, "in_progress")}>Start</CompactButton>}
                  {job.status === "in_progress" && <CompactButton dark onClick={() => openDeliveryModal(job)} data-testid={`deliver-btn-${job.id}`}>Send Preview</CompactButton>}
                  {job.status === "approvedForFinalDelivery" && <CompactButton dark onClick={() => openDeliveryModal(job)} data-testid={`deliver-final-btn-${job.id}`}>Deliver Gallery</CompactButton>}
                  {job.status === "completed" && <CompactButton onClick={() => openDeliveryModal(job)} data-testid={`add-more-btn-${job.id}`}>Add More</CompactButton>}
                </div>
              ))}
            </article>
          </div>

          <aside className="aura-pro-stack">
            <article className="aura-pro-panel aura-pro-side-calendar">
              <h3 className="serif">{monthNames[availabilityMonth - 1]} {availabilityYear}</h3>
              <div className="aura-pro-calendar-mini">
                {Array.from({ length: 35 }).map((_, index) => {
                  const day = index + 1;
                  const dateStr = `${availabilityYear}-${String(availabilityMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  return <span key={index} className={myAvailability.includes(dateStr) ? "is-booked" : ""}>{day <= 31 ? day : ""}</span>;
                })}
              </div>
            </article>
            <article className="aura-pro-panel">
              <div className="aura-pro-panel-head">
                <h3 className="serif">Pending Requests</h3>
                <StatusPill tone="purple">{pendingBookings.length}</StatusPill>
              </div>
              {(pendingBookings.length ? pendingBookings : bookings.slice(0, 3)).slice(0, 3).map((booking) => (
                <button key={booking.id} className="aura-pro-request-row" onClick={() => setActiveSection("messages")}>
                  <Avatar name={booking.client_name} getInitials={getInitials} />
                  <span>
                    <strong>{booking.client_name}</strong>
                    <small>{booking.session_type || "Portrait Session"}</small>
                  </span>
                  <StatusPill tone="purple">New</StatusPill>
                </button>
              ))}
              {bookings.length === 0 && <p className="aura-pro-muted">New booking requests will appear here.</p>}
            </article>
          </aside>
        </div>
      </section>
    );
  }

  if (activeSection === "availability") {
    const daysInMonth = new Date(availabilityYear, availabilityMonth, 0).getDate();
    const firstDay = new Date(availabilityYear, availabilityMonth - 1, 1).getDay();
    const today = new Date();
    const selectedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const calendarCells = [
      ...Array.from({ length: firstDay }, (_, index) => ({ key: `empty-${index}` })),
      ...Array.from({ length: daysInMonth }, (_, index) => {
        const day = index + 1;
        const dateStr = `${availabilityYear}-${String(availabilityMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        return { key: dateStr, day, dateStr, available: myAvailability.includes(dateStr), selected: dateStr === selectedDate };
      })
    ];
    return (
      <section className="aura-pro-section" data-testid="availability-section">
        <SectionHead title="Availability" subtitle="Set your schedule, manage booking windows, and control when clients can book." />
        <div className="aura-pro-stat-grid four">
          <StatCard icon={CalendarPlus} label="Available Days" value={`${myAvailability.length}`} trend="12.5% from last month" />
          <StatCard icon={Users} label="Booked This Month" value={activeBookings.length || 0} trend="9.1% from last month" bars />
          <StatCard icon={Send} label="Pending Requests" value={pendingBookings.length} detail={`${pendingBookings.length} need response`} />
          <StatCard icon={Lock} label="Blackout Dates" value="4" detail="View & manage" />
        </div>

        <div className="aura-pro-layout calendar">
          <article className="aura-pro-panel aura-availability-calendar">
            <div className="aura-pro-calendar-head">
              <button onClick={() => {
                if (availabilityMonth === 1) {
                  setAvailabilityMonth(12);
                  setAvailabilityYear(availabilityYear - 1);
                } else {
                  setAvailabilityMonth(availabilityMonth - 1);
                }
              }}><ChevronLeft className="w-4 h-4" /></button>
              <h3 className="serif">{monthNames[availabilityMonth - 1]} {availabilityYear}</h3>
              <div className="aura-pro-tabs tight">
                <button className="is-active">Month</button>
                <button>Week</button>
                <button>Agenda</button>
              </div>
              <button onClick={() => {
                if (availabilityMonth === 12) {
                  setAvailabilityMonth(1);
                  setAvailabilityYear(availabilityYear + 1);
                } else {
                  setAvailabilityMonth(availabilityMonth + 1);
                }
              }}><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="aura-availability-grid">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <strong key={day}>{day}</strong>)}
              {calendarCells.map((cell) => cell.day ? (
                <button
                  key={cell.key}
                  onClick={() => toggleDateAvailability(cell.dateStr)}
                  className={`${cell.available ? "is-available" : ""} ${cell.selected ? "is-selected" : ""}`}
                  data-testid={`avail-day-${cell.day}`}
                >
                  <span>{cell.day}</span>
                  <i>{cell.available ? "Available" : "Open"}</i>
                </button>
              ) : <span key={cell.key} />)}
            </div>
            <div className="aura-pro-legend">
              <span><i className="green" />Available</span>
              <span><i className="amber" />Limited</span>
              <span><i className="purple" />Booked</span>
              <span><i />Blocked / Unavailable</span>
            </div>
          </article>

          <aside className="aura-pro-stack">
            <article className="aura-pro-panel">
              <div className="aura-pro-panel-head">
                <h3 className="serif">Selected Day</h3>
                <StatusPill tone="green">Available</StatusPill>
              </div>
              <p className="aura-pro-muted">Thu, June 4, 2026</p>
              <div className="aura-time-slots">
                {["9:00 AM", "11:00 AM", "2:00 PM", "4:00 PM", "6:00 PM"].map((slot, index) => (
                  <button key={slot} className={index === 3 ? "is-booked" : ""}>{slot}<small>{index === 3 ? "Booked" : "Available"}</small></button>
                ))}
              </div>
            </article>
            <article className="aura-pro-panel">
              <h3 className="serif">Booking Preferences</h3>
              {["Session Duration: 2 hours", "Buffer Time: 30 min", "Max Bookings: 2 sessions", "Advance Notice: 48 hours"].map((item) => (
                <div key={item} className="aura-pro-pref-row"><span>{item.split(":")[0]}</span><strong>{item.split(":")[1]}</strong></div>
              ))}
            </article>
          </aside>
        </div>
      </section>
    );
  }

  if (activeSection === "messages") {
    const thread = selectedConversation || filteredConversations[0] || conversations[0] || null;
    return (
      <section className="aura-pro-section" data-testid="messages-section">
        <SectionHead title="Messages" subtitle="Manage client conversations, inquiries, and delivery updates." />
        <div className="aura-pro-stat-grid three">
          <StatCard icon={MessageSquare} label="Unread" value={stats?.unread_messages || 0} detail="2 from yesterday" />
          <StatCard icon={Users} label="Awaiting Reply" value={conversations.length || 0} trend="1 from yesterday" bars />
          <StatCard icon={CheckCircle} label="Resolved This Week" value="18" trend="6 from last week" />
        </div>

        <div className="aura-messages-layout">
          <article className="aura-pro-panel aura-inbox-panel">
            <div className="aura-pro-panel-head">
              <h3 className="serif">Inbox</h3>
              <StatusPill>All Conversations</StatusPill>
            </div>
            <label className="aura-pro-search full">
              <Search className="w-4 h-4" />
              <input
                placeholder="Search messages..."
                value={messageQuery}
                onChange={(event) => setMessageQuery(event.target.value)}
              />
            </label>
            <div className="aura-pro-tabs wrap">
              {["All", "Unread", "Clients", "Inquiries", "Delivered"].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setMessageFilter(tab)}
                  className={messageFilter === tab ? "is-active" : ""}
                >
                  {tab}
                </button>
              ))}
            </div>
            {conversations.length === 0 ? (
              <EmptyState icon={MessageSquare} title="No messages yet" copy="Client inquiries and gallery delivery conversations will appear here." />
            ) : filteredConversations.length === 0 ? (
              <EmptyState icon={Search} title="No conversations match" copy="Try a different search term or message filter." />
            ) : (
              <div className="aura-conversation-list">
                {filteredConversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => openChat(conv)}
                    className={thread?.id === conv.id ? "is-active" : ""}
                    data-testid={`message-${conv.id}`}
                  >
                    <Avatar name={conv.client_name} getInitials={getInitials} image={conv.client_img} getImageUrl={getImageUrl} />
                    <span>
                      <strong>{conv.client_name}</strong>
                      <small>{conv.last_message || "No messages yet"}</small>
                    </span>
                    <em>{conv.last_message_at ? shortDate(conv.last_message_at, "Now") : "Now"}</em>
                  </button>
                ))}
              </div>
            )}
          </article>

          <article className="aura-pro-panel aura-chat-panel">
            {thread ? (
              <>
                <div className="aura-chat-head">
                  <Avatar name={thread.client_name} getInitials={getInitials} />
                  <div>
                    <h3 className="serif">{thread.client_name}</h3>
                    <p>{thread.session_type || "Portrait Session"} • {shortDate(thread.created_at || thread.last_message_at)}</p>
                  </div>
                  <CompactButton onClick={() => setActiveSection("bookings")}>View Booking</CompactButton>
                </div>
                <div className="aura-chat-stream">
                  {chatMessages.length === 0 ? (
                    <div className="aura-chat-bubble theirs">{thread.last_message || "Select a conversation to view the full thread."}</div>
                  ) : chatMessages.map((message, index) => (
                    <div key={index} className={`aura-chat-bubble ${message.sender_type === "photographer" ? "mine" : "theirs"}`}>
                      {message.content}
                    </div>
                  ))}
                </div>
                <div className="aura-chat-composer">
                  <textarea
                    value={newMessage}
                    onChange={(event) => setNewMessage(event.target.value)}
                    placeholder={selectedConversation ? "Type your message..." : "Choose a conversation to reply..."}
                    disabled={!selectedConversation}
                  />
                  <div>
                    <CompactButton onClick={() => setActiveSection("galleries")}><Copy className="w-4 h-4" /> Send Gallery Link</CompactButton>
                    <CompactButton onClick={() => setNewMessage("Yes, the full gallery will be delivered by Saturday evening. You will receive an email as soon as it's ready.")}>
                      <Sparkles className="w-4 h-4" /> Quick Reply
                    </CompactButton>
                    <CompactButton dark onClick={sendChatMessage} disabled={!newMessage.trim() || !selectedConversation || sendingMessage} data-testid="send-message-inline-btn">
                      Send <Send className="w-4 h-4" />
                    </CompactButton>
                  </div>
                </div>
              </>
            ) : (
              <EmptyState icon={MessageSquare} title="No conversation selected" copy="Choose a client thread to review messages and send gallery links." />
            )}
          </article>

          <aside className="aura-pro-stack">
            <article className="aura-pro-panel">
              <h3 className="serif">Related Project</h3>
              <div className="aura-related-project">
                <img src={getImageUrl(imagePool[0])} alt="Related project" />
                <div><strong>Portrait Session</strong><small>Studio • Natural Light</small></div>
              </div>
              {["Gallery Status: Editing", "Payment Status: Paid in Full", "Contract: Signed"].map((item) => (
                <div key={item} className="aura-pro-pref-row"><span>{item.split(":")[0]}</span><strong>{item.split(":")[1]}</strong></div>
              ))}
              <CompactButton dark onClick={() => setActiveSection("galleries")} className="full">Open Gallery</CompactButton>
            </article>
            <article className="aura-pro-panel aura-ai-reply">
              <h3><Sparkles className="w-4 h-4" /> Smart Reply Assistant</h3>
              <p>Save time with refined responses.</p>
              <button onClick={() => setNewMessage("Yes, the full gallery will be delivered by Saturday evening. You will receive an email as soon as it's ready.")}>Use This Reply</button>
            </article>
          </aside>
        </div>
      </section>
    );
  }

  if (activeSection === "portfolio") {
    return (
      <section className="aura-pro-section" data-testid="portfolio-section">
        <SectionHead title="Portfolio" subtitle="Curate, organize, and showcase your best work." />
        <div className="aura-pro-stat-grid four">
          <StatCard icon={Images} label="Portfolio Images" value={portfolioImages.length} trend="+18 this month" />
          <StatCard icon={Camera} label="Collections" value="6" trend="+1 this month" />
          <StatCard icon={Heart} label="Profile Saves" value="342" trend="12.5% from last month" />
          <StatCard icon={Eye} label="Views This Month" value={formattedProfileViews || "2.2k"} trend="18.6% from last month" />
        </div>

        <div className="aura-pro-layout portfolio">
          <div className="aura-pro-stack">
            <article className="aura-pro-panel aura-upload-command">
              <input ref={portfolioInputRef} type="file" accept="image/*" className="hidden" onChange={handlePortfolioUpload} />
              <button onClick={() => portfolioInputRef.current?.click()} disabled={uploading} data-testid="upload-portfolio-btn">
                <Upload className="w-7 h-7" />
                <strong>{uploading ? "Uploading..." : "Upload Images"}</strong>
                <small>Drag & drop or click to upload JPG, PNG up to 50MB</small>
              </button>
              <div className="aura-url-import">
                <input value={newImageUrl} onChange={(event) => setNewImageUrl(event.target.value)} placeholder="Paste image URL..." data-testid="portfolio-image-input" />
                <CompactButton onClick={addPortfolioImage} disabled={!newImageUrl.trim()} data-testid="add-portfolio-image">Add URL</CompactButton>
              </div>
            </article>
            <article className="aura-pro-panel aura-portfolio-grid-panel">
              <div className="aura-pro-toolbar">
                <label className="aura-pro-search">
                  <Search className="w-4 h-4" />
                  <input
                    placeholder="Search portfolio images..."
                    value={portfolioQuery}
                    onChange={(event) => setPortfolioQuery(event.target.value)}
                  />
                </label>
                <div className="aura-pro-tabs">
                  {["All", "Weddings", "Portraits", "Branding", "Editorial"].map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setPortfolioFilter(tab)}
                      className={portfolioFilter === tab ? "is-active" : ""}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>
              {portfolioImages.length === 0 ? (
                <EmptyState icon={Images} title="No portfolio images" copy="Add your strongest work to improve search visibility and public profile trust." />
              ) : portfolioItems.length === 0 ? (
                <EmptyState icon={Search} title="No portfolio images match" copy="Clear the search or choose another collection filter." />
              ) : (
                <div className="aura-portfolio-card-grid">
                  {portfolioItems.map(({ img, index, title, category }) => (
                    <article key={`${img}-${index}`} className="aura-portfolio-item">
                      <img src={getImageUrl(img)} alt={`Portfolio ${index + 1}`} />
                      {index === 0 && <StatusPill tone="purple">Featured</StatusPill>}
                      <div>
                        <h3 className="serif">{title}</h3>
                        <p>{category} • {12 + index} images</p>
                      </div>
                      <button onClick={() => removePortfolioImage(index)} data-testid={`remove-portfolio-${index}`}><X className="w-4 h-4" /></button>
                    </article>
                  ))}
                </div>
              )}
            </article>
          </div>
          <aside className="aura-pro-stack">
            <article className="aura-pro-panel">
              <div className="aura-pro-panel-head"><h3 className="serif">Portfolio Health</h3><strong>{profileCompletion}%</strong></div>
              <Progress value={profileCompletion} />
              <div className="aura-health-tip compact">
                <span><Sparkles className="w-5 h-5" /></span>
                <p>Great progress! Add more portfolio images to attract more clients.</p>
                <button onClick={() => portfolioInputRef.current?.click()}>Optimize now <ArrowRight className="w-4 h-4" /></button>
              </div>
            </article>
            <article className="aura-pro-panel">
              <div className="aura-pro-panel-head"><h3 className="serif">Top Performing Collection</h3><ChevronRight className="w-4 h-4" /></div>
              <div className="aura-related-project">
                <img src={getImageUrl(imagePool[0])} alt="Top collection" />
                <div><strong>Timeless Romance</strong><small>Views this month</small></div>
                <strong className="serif">842</strong>
              </div>
              <CompactButton onClick={() => notify("Portfolio insights are included in your performance dashboard.")} className="full">View Insights</CompactButton>
            </article>
          </aside>
        </div>
      </section>
    );
  }

  if (activeSection === "profile") {
    return (
      <section className="aura-pro-section" data-testid="profile-section">
        <SectionHead title="Profile" subtitle="Shape how clients discover and trust your studio." />
        <div className="aura-pro-stat-grid four">
          <StatCard icon={CheckCircle} label="Profile Completion" value={`${profileCompletion}%`} detail="Almost there! Keep going." />
          <StatCard icon={Eye} label="Profile Views" value={formattedProfileViews || "2.4k"} trend="18.6% from last month" />
          <StatCard icon={MessageSquare} label="New Inquiries" value={stats?.active_inquiries || 0} trend="27.3% from last month" />
          <StatCard icon={Lock} label="Saves This Month" value="326" trend="21.8% from last month" />
        </div>

        <div className="aura-pro-layout profile">
          <div className="aura-pro-stack">
            <article className="aura-pro-panel aura-profile-hero-card">
              <div className="aura-profile-logo">
                {getImageUrl(profileImage) ? <img src={getImageUrl(profileImage)} alt={studioName} /> : <span>{getInitials(studioName)}</span>}
                <input ref={profileInputRef} type="file" accept="image/*" className="hidden" onChange={handleProfilePhotoUpload} />
                <button onClick={() => profileInputRef.current?.click()} disabled={uploading} data-testid="upload-profile-photo-btn">
                  <Upload className="w-4 h-4" />
                </button>
              </div>
              <div>
                <h3 className="serif">{studioName}</h3>
                <p>{user?.location || "Jamaica"} • {user?.category || "Portrait"} • Wedding • Branding</p>
                <small>{user?.bio || "Natural light portraiture and timeless storytelling for modern clients."}</small>
                <div className="aura-pro-tags">
                  {["Portraits", "Weddings", "Branding", "Editorial"].map((tag) => <span key={tag}>{tag}</span>)}
                </div>
                <div className="aura-pro-action-row left">
                  <CompactButton dark onClick={() => profileInputRef.current?.click()}><Edit className="w-4 h-4" /> Edit Profile</CompactButton>
                  <Link to={`/photographer/${user?.id}`} className="aura-pro-btn"><Eye className="w-4 h-4" /> Preview Public Page</Link>
                </div>
              </div>
            </article>

            <div className="aura-pro-card-columns">
              <article className="aura-pro-panel"><h3 className="serif">About</h3><p>{user?.bio || "We capture real moments with an editorial eye and a calm approach. Based in Jamaica, we specialize in portraits, weddings, and brand storytelling."}</p></article>
              <article className="aura-pro-panel"><h3 className="serif">Contact & Links</h3><p>{user?.email || "hello@focuspointimages.com"}<br />{user?.location || "Kingston, Jamaica"}</p></article>
              <article className="aura-pro-panel"><h3 className="serif">Services & Specialties</h3><div className="aura-pro-tags">{["Weddings", "Portraits", "Branding", "Editorial", "Studio", "Outdoor"].map((tag) => <span key={tag}>{tag}</span>)}</div></article>
            </div>

            <article className="aura-pro-panel">
              <div className="aura-pro-panel-head"><h3 className="serif">Featured Work</h3><button onClick={() => setActiveSection("portfolio")}>Edit</button></div>
              <div className="aura-featured-work-row">
                {imagePool.slice(0, 4).map((img, index) => <img key={`${img}-${index}`} src={getImageUrl(img)} alt={`Featured work ${index + 1}`} />)}
              </div>
            </article>
          </div>

          <aside className="aura-pro-stack">
            <article className="aura-pro-panel">
              <div className="aura-pro-panel-head"><h3 className="serif">Profile Health</h3><strong>{profileCompletion}%</strong></div>
              <Progress value={profileCompletion} />
              {["Add bio", "Add contact links", "Add specialties", "Upload profile image"].map((item) => (
                <div className="aura-check-row" key={item}><Check className="w-4 h-4" /> {item} <CheckCircle className="w-4 h-4" /></div>
              ))}
            </article>
            <article className="aura-pro-panel">
              <h3 className="serif">Discover Preview</h3>
              <div className="aura-related-project">
                <div className="aura-profile-logo mini">{getImageUrl(profileImage) ? <img src={getImageUrl(profileImage)} alt="" /> : <span>{getInitials(studioName)}</span>}</div>
                <div><strong>{studioName}</strong><small>{user?.location || "Jamaica"} • Portrait • Wedding</small></div>
              </div>
              <Link to={`/photographer/${user?.id}`} className="aura-pro-text-link">View full public page <ArrowRight className="w-4 h-4" /></Link>
            </article>
          </aside>
        </div>
      </section>
    );
  }

  if (activeSection === "earnings") {
    const transactions = completedJobs.length ? completedJobs : jobs.slice(0, 5);
    return (
      <section className="aura-pro-section" data-testid="earnings-section">
        <SectionHead title="Earnings" subtitle="Track revenue, payouts, and financial performance across your studio." />
        <div className="aura-pro-stat-grid four">
          <StatCard icon={DollarSign} label="Total Earnings" value={money(totalRevenue)} trend="18.6% from last month" />
          <StatCard icon={Wallet} label="Pending Payouts" value={money(Math.round(totalRevenue * 0.15))} detail="Next payout Friday" />
          <StatCard icon={Camera} label="Completed Jobs" value={stats?.completed_jobs || completedJobs.length || 0} detail="3 this week" />
          <StatCard icon={BarChart3} label="Avg. Per Job" value={money(averageJob)} trend="9.4% from last month" />
        </div>

        <div className="aura-pro-layout earnings">
          <div className="aura-pro-stack">
            <article className="aura-pro-panel aura-revenue-panel">
              <div className="aura-pro-panel-head"><h3 className="serif">Revenue Overview</h3><div className="aura-pro-tabs tight"><button>Week</button><button className="is-active">Month</button><button>Year</button></div></div>
              <div className="aura-revenue-chart">
                {[24, 42, 54, 38, 62, 74, 66, 88, 66, 78, 54, 72].map((point, index) => <i key={index} style={{ height: `${point}%` }} />)}
              </div>
              <div className="aura-earnings-snapshot">
                <span><Star className="w-5 h-5" /><strong className="serif">July 2024</strong><small>Best Month</small></span>
                <span><Wallet className="w-5 h-5" /><strong className="serif">{money(totalRevenue + 1420)}</strong><small>Gross Revenue</small></span>
                <span><CreditCard className="w-5 h-5" /><strong className="serif">{money(totalRevenue)}</strong><small>Net After Fees</small></span>
              </div>
            </article>
            <article className="aura-pro-panel">
              <div className="aura-pro-panel-head"><h3 className="serif">Recent Transactions</h3><button>View All</button></div>
              {transactions.length === 0 ? (
                <EmptyState icon={Wallet} title="No transactions yet" copy="Completed jobs and payouts will appear here." />
              ) : (
                <div className="aura-pro-table">
                  {transactions.map((job, index) => (
                    <div key={job.id || index}>
                      <Avatar name={job.client_name || job.title} getInitials={getInitials} />
                      <span><strong>{job.client_name || job.title}</strong><small>{job.title || "Portrait Session"}</small></span>
                      <span>{shortDate(job.completed_at || job.created_at)}</span>
                      <StatusPill tone={job.status === "completed" ? "green" : "purple"}>{job.status === "completed" ? "Paid" : statusLabel(job.status)}</StatusPill>
                      <strong className="is-money">+ {money((user?.hourly_rate || 200) * 8)}</strong>
                    </div>
                  ))}
                </div>
              )}
            </article>
          </div>
          <aside className="aura-pro-stack">
            <article className="aura-pro-panel"><h3 className="serif">Payout Schedule</h3><p className="aura-pro-large serif">Fri, May 31</p><p className="aura-pro-muted">Bank transfer ending in 0421</p><CompactButton dark className="full" onClick={() => notify("Payout schedule is ready for your finance dashboard.")}>View Payouts</CompactButton></article>
            <article className="aura-pro-panel"><h3 className="serif">Income Breakdown</h3><div className="aura-donut" /><div className="aura-breakdown-list">{["Weddings 52%", "Portraits 24%", "Branding 18%", "Editorial 6%"].map((item) => <span key={item}>{item}</span>)}</div></article>
            <article className="aura-pro-panel"><h3 className="serif">Top Earning Service</h3><p className="aura-pro-large serif">Weddings<br />{money(Math.round(totalRevenue * 0.52))}</p><MiniSparkline /></article>
          </aside>
        </div>
      </section>
    );
  }

  if (activeSection === "membership") {
    const currentPlan = subscription?.plan || "standard";
    const planName = subscription?.plan_details?.name || (currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1));
    const planCards = [
      { id: "standard", label: "Standard", price: "Free", tag: currentPlan === "standard" ? "Current Plan" : "", features: ["10 Portfolio Images", "20% Marketplace Fee", "No Monthly Boosts", "No Verified Badge"] },
      { id: "premium", label: "Premium", price: "$29 /mo", tag: "Popular", features: ["Unlimited Portfolio", "10% Marketplace Fee", "2 Profile Boosts / mo", "Verified Pro Badge"] },
      { id: "elite", label: "Elite", price: "$99 /mo", tag: "Pro", features: ["0% Transaction Fees", "Weekly Spotlight", "Priority Brand Matching", "Custom aura.pro URL"] }
    ];
    return (
      <section className="aura-pro-section" data-testid="membership-section">
        <SectionHead title="Membership" subtitle="Manage your plan, visibility tools, and growth benefits." />
        {subscriptionLoading && !subscription ? (
          <div className="aura-pro-loading">Loading membership...</div>
        ) : (
          <>
            <div className="aura-pro-stat-grid four">
              <StatCard icon={Crown} label="Current Plan" value={planName} detail={currentPlan === "standard" ? "Free plan" : "Active plan"} />
              <StatCard icon={Eye} label="Profile Visibility" value="68%" trend="12% from last month" />
              <StatCard icon={Zap} label="Boosts Remaining" value={subscription?.boosts_remaining || 0} detail="Out of 2 this month" />
              <StatCard icon={BarChart3} label="Monthly Impressions" value="2.8k" trend="18.6% from last month" />
            </div>
            <article className="aura-membership-banner">
              <div><p>Current Plan</p><h3 className="serif">{planName}</h3><span>A great start for building your presence and showcasing your work.</span></div>
              <div><strong className="serif">{portfolioImages.length} of 10</strong><span>Portfolio images used</span><Progress value={Math.min(100, portfolioImages.length * 10)} /></div>
              <div><strong className="serif">20%</strong><span>Marketplace fee</span></div>
              <div><strong className="serif">No Verified Badge</strong><span>Upgrade to Premium or Elite to unlock</span></div>
            </article>
            <div className="aura-plan-grid">
              {planCards.map((plan) => (
                <article key={plan.id} className={`aura-plan-card ${currentPlan === plan.id ? "is-current" : ""}`} data-testid={`plan-${plan.id}`}>
                  <div className="aura-pro-panel-head"><p>{plan.label}</p>{plan.tag && <StatusPill tone="purple">{plan.tag}</StatusPill>}</div>
                  <h3 className="serif">{plan.price}</h3>
                  {plan.features.map((feature) => <span key={feature}><Check className="w-4 h-4" /> {feature}</span>)}
                  <CompactButton
                    dark={currentPlan !== plan.id}
                    onClick={() => handleChangePlan(plan.id)}
                    disabled={currentPlan === plan.id || subscriptionLoading}
                    data-testid={`select-${plan.id}-btn`}
                    className="full"
                  >
                    {currentPlan === plan.id ? "Current Plan" : `Upgrade to ${plan.label}`}
                  </CompactButton>
                </article>
              ))}
            </div>
            <div className="aura-pro-card-columns membership">
              <article className="aura-pro-panel aura-spotlight-card">
                <h3>Aura Spotlight</h3>
                <p>Boost your profile to the top of search results for 48 hours.</p>
                <div><strong className="serif">{subscription?.boosts_remaining || 0}</strong><span>Boosts available</span></div>
                <CompactButton dark onClick={handleActivateBoost} disabled={subscriptionLoading || subscription?.is_boosted || (subscription?.boosts_remaining || 0) <= 0} data-testid="activate-boost-btn">
                  {subscription?.is_boosted ? "Spotlight Active" : "Use Boost"}
                </CompactButton>
              </article>
              <article className="aura-pro-panel"><h3 className="serif">Buy More Boosts</h3>{["single", "pack3", "pack5"].map((pack) => <button key={pack} className="aura-boost-row" onClick={() => handlePurchaseBoosts(pack)} disabled={subscriptionLoading} data-testid={`buy-${pack}-boost`}>{pack === "single" ? "1 Boost" : pack === "pack3" ? "3 Boosts" : "5 Boosts"} <strong>{formatBoostPrice(boostPricing[pack])}</strong></button>)}</article>
              <article className="aura-pro-panel"><h3 className="serif">Billing & Renewal</h3><div className="aura-pro-pref-row"><span>Plan</span><strong>{planName}</strong></div><div className="aura-pro-pref-row"><span>Renews On</span><strong>-</strong></div><CompactButton onClick={() => notify("Billing management is connected to your subscription settings.")} className="full">Manage Billing</CompactButton></article>
            </div>
          </>
        )}
      </section>
    );
  }

  return null;
};

export default ProRedesignSections;
